import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useRental, rentalKeys } from '../../entities/rental';
import { Row, Col, Card, Table, Button, Nav } from 'react-bootstrap';
import { getContrastColor, COLOR_SUCCESS, COLOR_WARNING, COLOR_PRIMARY, COLOR_DANGER, COLOR_SECONDARY, COLOR_DARK } from '../../shared/colorUtils';
import { useDateFormat } from '../../shared/DateFormatContext';
import { useTranslation } from 'react-i18next';
import { tariffTypeHumanify } from './utils/tariffType';
import Calendar from './components/Calendar';
import Statistics from './components/Statistics';
import { useCurrency } from '../../shared/CurrencyContext';
import RentalReturnModal from './components/RentalReturnModal';
import { IconCalendarEvent, IconChartBar, IconInfoCircle, IconPackage } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import { CenteredLoader } from '../../components/LoadingSpinner';
import EmptyState from '../../components/feedback/EmptyState';


const RentalDetail = () => {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrency();
  const { formatDate, formatDateTime } = useDateFormat();
  const { id } = useParams();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('details');
  const [showReturnModal, setShowReturnModal] = useState(false);

  const { isPending: loading, error: rentalError, data: rental } = useRental(id);
  const rentalItems = rental?.rental_items ?? [];

  const getStatusColor = (status) => {
    const statusMap = {
      'active': COLOR_SUCCESS,
      'inactive': COLOR_WARNING,
      'completed': COLOR_PRIMARY,
      'expired': COLOR_DANGER,
      'canceled': COLOR_SECONDARY
    };
    return statusMap[status] || '#adb5bd';
  };

  const handleReturnComplete = () => {
    queryClient.invalidateQueries({ queryKey: rentalKeys.detail(id) });
    setShowReturnModal(false);
  };

  if (loading) {
    return (
      <PageShell pretitle={t('rentals.title')} title={t('rentals.detail.title')}>
        <PageSection>
          <CenteredLoader />
        </PageSection>
      </PageShell>
    );
  }

  if (!rental) {
    return (
      <PageShell pretitle={t('rentals.title')} title={t('rentals.detail.title')}>
        <PageSection>
          <EmptyState
            title={t('rentals.detail.notFound')}
            message={t('rentals.detail.notFoundMessage', 'Požadovaná zápůjčka nebyla nalezena nebo už není dostupná.')}
            action={<Link to="/rentals" className="btn btn-primary">{t('common.back')}</Link>}
          />
        </PageSection>
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('rentals.detail.title')}
      title={<>{t('rentals.rental')} #{rental.id} <span className="badge ms-2" style={{ backgroundColor: getStatusColor(rental.rental_status), color: getContrastColor(getStatusColor(rental.rental_status)) }}>{t(`rentals.statuses.${rental.rental_status}`)}</span></>}
      description={t('rentals.detail.subtitle', 'Detail zápůjčky, přehled položek, kalendář dostupnosti a související statistiky.')}
      actions={
        <>
          {rental.rental_status !== 'completed' && rental.rental_status !== 'canceled' && (
            <Button
              variant="success"
              onClick={() => setShowReturnModal(true)}
              className="d-flex align-items-center"
            >
              <IconPackage className="me-2" size={18} />
              {t('rentals.return.buttonTitle')}
            </Button>
          )}
          <Button as={Link} to={`/rentals/rental/${rental.id}/settings`} variant="primary">
            {t('rentals.detail.settings')}
          </Button>
          <Button as={Link} to="/rentals" variant="outline-secondary">
            {t('common.back')}
          </Button>
        </>
      }
    >
      <div className="card app-surface">
        <div className="card-body p-3">
          <Nav className="app-tabnav">
            <Nav.Item>
              <Nav.Link as="button" type="button" active={activeTab === 'details'} onClick={() => setActiveTab('details')}>
                <IconInfoCircle size={18} />
                {t('rentals.detail.tabs.details')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link as="button" type="button" active={activeTab === 'calendar'} onClick={() => setActiveTab('calendar')}>
                <IconCalendarEvent size={18} />
                {t('rentals.detail.tabs.calendar')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link as="button" type="button" active={activeTab === 'statistics'} onClick={() => setActiveTab('statistics')}>
                <IconChartBar size={18} />
                {t('rentals.detail.tabs.statistics')}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <div className="pt-3">
        {activeTab === 'details' && (
          <Row className="g-3">
            {/* Rental Information */}
            <Col lg={6}>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">{t('rentals.detail.rentalInfo')}</h5>
                </Card.Header>
                <Card.Body>
                  <Table responsive>
                    <tbody>
                      <tr>
                        <th>{t('rentals.detail.customer')}</th>
                        <td>
                          <Link to={`/customers/customer/${rental.customer}`}>
                            {rental.customer_details.resp_person}
                          </Link>
                        </td>
                      </tr>
                      <tr>
                        <th>{t('rentals.detail.dateFrom')}</th>
                        <td>{formatDateTime(rental.date_from)}</td>
                      </tr>
                      <tr>
                        <th>{t('rentals.detail.dateTo')}</th>
                        <td>{formatDateTime(rental.date_to)}</td>
                      </tr>
                      <tr>
                        <th>{t('rentals.detail.rentalDate')}</th>
                        <td>{formatDate(rental.rental_date)}</td>
                      </tr>
                      <tr>
                        <th>{t('rentals.detail.reason')}</th>
                        <td>{rental.rental_reason_details.name}</td>
                      </tr>
                      <tr>
                        <th>{t('rentals.detail.issuedBy')}</th>
                        <td>{rental.issued_by_details.display_name}</td>
                      </tr>
                      <tr>
                        <th>{t('rentals.detail.status')}</th>
                        <td>
                          <span className="badge" style={{ backgroundColor: getStatusColor(rental.rental_status), color: getContrastColor(getStatusColor(rental.rental_status)) }}>
                            {t(`rentals.statuses.${rental.rental_status}`)}
                          </span>
                        </td>
                      </tr>
                      {rental.note && (
                        <tr>
                          <th>{t('rentals.detail.note')}</th>
                          <td>{rental.note}</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            {/* Financial Summary */}
            <Col lg={6}>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">{t('rentals.detail.financialSummary')}</h5>
                </Card.Header>
                <Card.Body>
                  <Table responsive>
                    <tbody>
                      <tr>
                        <th>{t('rentals.detail.price')}</th>
                        <td>
                          {rental.currency_detail
                            ? formatCurrency(rental.price || 0, { currency: rental.currency_detail })
                            : formatCurrency(rental.price || 0)}
                        </td>
                      </tr>
                      {rental.discount_percentage > 0 && (
                        <tr>
                          <th>{t('rentals.detail.discount')}</th>
                          <td>{rental.discount_percentage}%</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            {/* Rental Items */}
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">{t('rentals.detail.rentalItems')}</h5>
                </Card.Header>
                <Card.Body>
                  {rentalItems.length === 0 ? (
                    <div className="alert alert-info">
                      {t('rentals.detail.noItems')}
                    </div>
                  ) : (
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>{t('rentals.detail.itemName')}</th>
                          <th>{t('rentals.detail.quantity')}</th>
                          <th>{t('rentals.detail.units')}</th>
                          <th>{t('rentals.detail.unitType')}</th>
                          <th>{t('rentals.detail.pricePerUnit')}</th>
                          <th>{t('rentals.detail.subtotal')}</th>
                          <th>{t('rentals.return.returnStatus')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rentalItems.map(item => (
                          <tr key={item.id}>
                            <td>
                              <Link to={item.stock_item_product ? `/warehouses/products/${item.stock_item_product}` : '/warehouses/products'}>
                                {item.stock_item_product_name || '-'}
                              </Link>
                            </td>
                            <td>{item.quantity}</td>
                            <td>{item.tariff_quantity || 1}</td>
                            <td>{tariffTypeHumanify(item.tariff_type || 'day', t)}</td>
                            <td>
                              {rental.currency_detail
                                ? formatCurrency(item.tariff_price || 0, { currency: rental.currency_detail })
                                : formatCurrency(item.tariff_price || 0)}
                            </td>
                            <td>
                              {rental.currency_detail
                                ? formatCurrency(item.total_price || (item.tariff_price || 0) * item.quantity, { currency: rental.currency_detail })
                                : formatCurrency(item.total_price || (item.tariff_price || 0) * item.quantity)}
                            </td>
                            <td>
                              {item.quantity_pending > 0 ? (
                                <span className="badge me-1" style={{ backgroundColor: COLOR_WARNING, color: getContrastColor(COLOR_WARNING) }}>
                                  {t('rentals.return.pending')}: {item.quantity_pending}
                                </span>
                              ) : null}
                              {item.quantity_returned > 0 ? (
                                <span className="badge me-1" style={{ backgroundColor: COLOR_SUCCESS, color: getContrastColor(COLOR_SUCCESS) }}>
                                  {t('rentals.return.quantityReturned')}: {item.quantity_returned}
                                </span>
                              ) : null}
                              {item.quantity_damaged > 0 ? (
                                <span className="badge me-1" style={{ backgroundColor: COLOR_DANGER, color: getContrastColor(COLOR_DANGER) }}>
                                  {t('rentals.return.quantityDamaged')}: {item.quantity_damaged}
                                </span>
                              ) : null}
                              {item.quantity_lost > 0 ? (
                                <span className="badge me-1" style={{ backgroundColor: COLOR_SECONDARY, color: getContrastColor(COLOR_SECONDARY) }}>
                                  {t('rentals.return.quantityLost')}: {item.quantity_lost}
                                </span>
                              ) : null}
                              {item.quantity_stolen > 0 ? (
                                <span className="badge me-1" style={{ backgroundColor: COLOR_DARK, color: getContrastColor(COLOR_DARK) }}>
                                  {t('rentals.return.quantityStolen')}: {item.quantity_stolen}
                                </span>
                              ) : null}
                              {item.is_fully_returned && (
                                <span className="badge" style={{ backgroundColor: COLOR_PRIMARY, color: getContrastColor(COLOR_PRIMARY) }}>
                                  {t('rentals.return.fullyReturned')}
                                </span>
                              )}
                              {!item.is_fully_returned && item.quantity_pending === item.quantity && (
                                <span className="badge" style={{ backgroundColor: COLOR_WARNING, color: getContrastColor(COLOR_WARNING) }}>
                                  {t('rentals.return.status.not_returned')}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {activeTab === 'calendar' && (
          <Calendar />
        )}

        {activeTab === 'statistics' && (
          <Statistics rental={rental} rentalItems={rentalItems} />
        )}
          </div>
        </div>
      </div>

      {/* Return Modal */}
      <RentalReturnModal
        show={showReturnModal}
        onHide={() => setShowReturnModal(false)}
        rental={{ ...rental, rental_items: rentalItems }}
        onReturnComplete={handleReturnComplete}
      />
    </PageShell>
  );
};

export default RentalDetail;
